import React, { useState } from 'react';
import { GeneratedImage } from '../hooks/useImageGeneration';
import CanvasDisplay from './CanvasDisplay';
import ImageUpload from './ImageUpload';
import './Homepage.css';

interface HomepageProps {
  images: GeneratedImage[];
  isGenerating: boolean;
  onClearImages: () => void;
  onDeleteImage: (imageId: string) => void;
  imageCount: number;
  onUploadImages?: (images: string[]) => void;
  onGenerateSticker?: (action: string) => void;
  userPhotos: string[];
}

const Homepage: React.FC<HomepageProps> = ({
  images,
  isGenerating,
  onClearImages,
  onDeleteImage,
  imageCount,
  onUploadImages,
  onGenerateSticker,
  userPhotos
}) => {
  const [showUpload, setShowUpload] = useState(false);

  const stickerActions = [
    { label: '🏃 Running', action: 'running' },
    { label: '💻 Coding', action: 'coding' },
    { label: '💃 Dancing', action: 'dancing' },
    { label: '🤔 Thinking', action: 'thinking' },
    { label: '😴 Sleeping', action: 'sleeping' },
    { label: '🎮 Gaming', action: 'gaming' },
    { label: '📚 Reading', action: 'reading' },
    { label: '🎵 Singing', action: 'singing' },
    { label: '🍕 Eating', action: 'eating' },
    { label: '✈️ Flying', action: 'flying' },
    { label: '🏊 Swimming', action: 'swimming' },
    { label: '🧘 Meditating', action: 'meditating' },
  ];

  // Use canvas display when we have images
  if (images.length > 0) {
    return (
      <>
        <CanvasDisplay images={images} onDeleteImage={onDeleteImage} onClearAll={onClearImages} />
        {/* Bottom button to add more stickers */}
        {userPhotos.length > 0 && onGenerateSticker && (
          <div style={{
            position: 'fixed',
            bottom: '0',
            left: '0',
            right: '0',
            padding: '20px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.3), transparent)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
          }}>
            <button
              onClick={() => setShowUpload(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 32px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '50px',
                cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)',
                minWidth: '200px',
              }}
            >
              + Add Sticker
            </button>
          </div>
        )}
        {showUpload && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px',
          }} onClick={() => setShowUpload(false)}>
            <div style={{
              background: 'white',
              padding: '40px',
              borderRadius: '20px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
            }} onClick={(e) => e.stopPropagation()}>
              <h2>Create a Sticker</h2>
              <div className="sticker-actions-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '12px',
                marginTop: '20px',
              }}>
                {stickerActions.map(({ label, action }) => (
                  <button
                    key={action}
                    onClick={() => {
                      if (onGenerateSticker) {
                        onGenerateSticker(action);
                      }
                      setShowUpload(false);
                    }}
                    disabled={isGenerating}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #667eea',
                      background: 'white',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      transition: 'all 0.2s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowUpload(false)}
                style={{
                  marginTop: '20px',
                  padding: '10px 20px',
                  background: '#e5e7eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Original grid view when no images - Upload flow first
  return (
    <div className="homepage" style={{
      position: 'relative',
      overflow: 'hidden',
      background: `
        linear-gradient(45deg, #FF6B6B 0%, #FFE66D 25%, #4ECDC4 50%, #95E1D3 75%, #FF6B6B 100%),
        repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 100px)
      `,
      animation: 'rainbow-slide 10s linear infinite',
    }}>
      {/* Colorful shapes background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}>
        {/* Circles */}
        <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255, 107, 107, 0.3)', top: '10%', left: '5%', filter: 'blur(20px)' }} />
        <div style={{ position: 'absolute', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255, 230, 109, 0.4)', top: '50%', right: '10%', filter: 'blur(15px)' }} />
        <div style={{ position: 'absolute', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(78, 205, 196, 0.3)', bottom: '10%', left: '15%', filter: 'blur(25px)' }} />
        <div style={{ position: 'absolute', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(149, 225, 211, 0.4)', top: '30%', left: '40%', filter: 'blur(20px)' }} />

        {/* Emoji/Sticker text elements */}
        <div style={{ position: 'absolute', fontSize: '120px', top: '5%', right: '20%', opacity: 0.2, transform: 'rotate(-15deg)' }}>🎨</div>
        <div style={{ position: 'absolute', fontSize: '100px', bottom: '15%', right: '5%', opacity: 0.2, transform: 'rotate(25deg)' }}>✨</div>
        <div style={{ position: 'absolute', fontSize: '90px', top: '40%', left: '10%', opacity: 0.15, transform: 'rotate(-10deg)' }}>🎭</div>
        <div style={{ position: 'absolute', fontSize: '110px', bottom: '30%', left: '60%', opacity: 0.2, transform: 'rotate(20deg)' }}>🌈</div>
        <div style={{ position: 'absolute', fontSize: '95px', top: '60%', right: '30%', opacity: 0.15, transform: 'rotate(-20deg)' }}>⭐</div>
        <div style={{ position: 'absolute', fontSize: '105px', top: '15%', left: '50%', opacity: 0.2, transform: 'rotate(15deg)' }}>🎪</div>
      </div>

      <style>{`
        @keyframes rainbow-slide {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      <header className="homepage-header" style={{ position: 'relative', zIndex: 1 }}>
        <h1 style={{ textShadow: '0 0 20px rgba(0,0,0,0.3), 0 0 40px rgba(255,255,255,0.5)' }}>AI Sticker Generator</h1>
        <p className="subtitle" style={{ textShadow: '0 0 10px rgba(0,0,0,0.3)' }}>Upload your photo and create custom stickers</p>
      </header>

      {isGenerating && (
        <div className="generation-indicator">
          <div className="spinner"></div>
          <p>Generating your sticker...</p>
        </div>
      )}

      <div className="image-grid" style={{ position: 'relative', zIndex: 1 }}>
        <div className="empty-state">
          {/* Step 1: Upload Photo */}
          {userPhotos.length === 0 ? (
            <>
              <h2>Step 1: Upload Your Photo</h2>
              <p style={{ marginBottom: '30px' }}>Upload a photo of yourself to get started</p>
              {onUploadImages && (
                <ImageUpload
                  onImagesUploaded={(images) => {
                    onUploadImages(images);
                  }}
                  currentImages={[]}
                />
              )}
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '20px' }}>
                Your photo stays private and is used only to generate your custom stickers
              </p>
            </>
          ) : (
            /* Step 2: Choose Sticker Actions */
            <>
              <h2>✅ Photo Uploaded!</h2>
              <p style={{ marginBottom: '30px' }}>Now choose what sticker you want to create:</p>
              <div className="sticker-actions-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '12px',
                maxWidth: '800px',
                margin: '0 auto',
              }}>
                {stickerActions.map(({ label, action }) => (
                  <button
                    key={action}
                    onClick={() => onGenerateSticker && onGenerateSticker(action)}
                    disabled={isGenerating}
                    style={{
                      padding: '16px',
                      border: '2px solid #667eea',
                      background: 'white',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#667eea';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.color = 'black';
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  if (onUploadImages) {
                    onUploadImages([]);
                  }
                }}
                style={{
                  marginTop: '30px',
                  padding: '10px 20px',
                  background: '#e5e7eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Change Photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homepage;